/**
 * <store-availability> — "Check store stock" drawer for the In Store Only block.
 *
 * Three-step flow that mirrors the design:
 *   1. Select Region
 *   2. Select a store within the region
 *   3. Show store details + a Size x Length stock grid for the current product
 *
 * Data source: the Retail Express Shopify-integration outlets endpoint, e.g.
 *   https://shopify.retailexpress.com.au/api/shopify_stores/{slug}/outlets?variant_ids={csv}
 *
 * This endpoint is public (CORS: *), needs no secret key, and is keyed by the
 * store slug. A single request returns every outlet with its per-variant stock
 * quantity, so we fetch once when the drawer opens and reuse the data for all
 * three steps. Regions are not in the response, so outlets are grouped by NZ
 * postcode (see #regionFor).
 *
 * Response shape:
 *   {
 *     "outlets": [
 *       {
 *         "name": "Newmarket",
 *         "external_id": 6,
 *         "stock": { "<variantId>": <qty>, ... },
 *         "contact": { "address1","address2","address3","suburb","postcode","phone","email", ... }
 *       }
 *     ]
 *   }
 */

const STATUS = {
  in: { mod: 'in', key: 'in_stock' },
  low: { mod: 'low', key: 'low_stock' },
  out: { mod: 'out', key: 'out_of_stock' },
};

/** NZ postcode -> region grouping used by the drawer's first step. */
const REGIONS = [
  { id: 'upper-north-island', name: 'Upper North Island', max: 4000 },
  { id: 'lower-north-island', name: 'Lower North Island', max: 7000 },
  { id: 'south-island', name: 'South Island', max: Infinity },
];

/** Escape a string for safe insertion into HTML. */
function esc(value) {
  return String(value ?? '').replace(
    /[&<>"']/g,
    (char) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char])
  );
}

class StoreAvailability extends HTMLElement {
  /** @type {AbortController | undefined} */
  #activeFetch;

  connectedCallback() {
    this.storeSlug = this.dataset.storeSlug || '';
    this.apiBase = (this.dataset.apiBase || 'https://shopify.retailexpress.com.au/api/shopify_stores').replace(
      /\/+$/,
      ''
    );
    this.lowThreshold = Number(this.dataset.lowThreshold || 3);
    this.sizeGuideUrl = this.dataset.sizeGuideUrl || '';
    this.sizeGuidePage = this.dataset.sizeGuidePage || '';

    /** @type {Record<string, string>} */
    this.labels = this.#readJson(this.querySelector('[data-sa-labels]')) || {};
    /** @type {Array<{id:string,size:string,length:string,available:boolean}>} */
    this.variants = (this.#readJson(this.querySelector('[data-sa-variants]')) || [])
      .filter((variant) => variant.id != null)
      .map((variant) => ({ ...variant, id: String(variant.id) }));

    const hasSuffixSizes = this.variants.some((v) => {
      const s = String(v.size || '');
      if (s.length < 2) return false;
      const lastChar = s.slice(-1).toLowerCase();
      if (s.toLowerCase() === 'xs' || s.toLowerCase() === 's') return false;
      return lastChar === 's' || lastChar === 'r' || lastChar === 't';
    });

    if (hasSuffixSizes) {
      const getLengthTypeForSize = (sizeValue) => {
        if (!sizeValue || sizeValue.length < 2) return 'regular';
        const lastChar = sizeValue.slice(-1).toLowerCase();
        if (sizeValue.toLowerCase() === 'xs' || sizeValue.toLowerCase() === 's') {
          return 'regular';
        }
        if (lastChar === 's') return 'short';
        if (lastChar === 't') return 'tall';
        if (lastChar === 'r') return 'regular';
        return 'regular';
      };

      const cleanSizeValue = (sizeValue) => {
        if (!sizeValue || sizeValue.length < 2) return sizeValue;
        const lastChar = sizeValue.slice(-1).toLowerCase();
        if (sizeValue.toLowerCase() === 'xs' || sizeValue.toLowerCase() === 's') {
          return sizeValue;
        }
        if (lastChar === 's' || lastChar === 'r' || lastChar === 't') {
          return sizeValue.slice(0, -1);
        }
        return sizeValue;
      };

      this.variants = this.variants.map((v) => {
        const sizeVal = v.size || '';
        const parsedLength = getLengthTypeForSize(sizeVal);
        const parsedSize = cleanSizeValue(sizeVal);
        const displayLength = parsedLength.charAt(0).toUpperCase() + parsedLength.slice(1);

        return {
          ...v,
          size: parsedSize,
          length: displayLength
        };
      });

      const lengthOrder = ['Short', 'Regular', 'Tall'];
      const uniqueParsedLengths = this.#unique(this.variants.map((v) => v.length));
      this.lengths = lengthOrder.filter(l => uniqueParsedLengths.includes(l));
      this.sizes = this.#unique(this.variants.map((v) => v.size));
    } else {
      this.sizes = this.#unique(this.variants.map((variant) => variant.size));
      this.lengths = this.#unique(this.variants.map((variant) => variant.length));
    }

    this.dialog = this.querySelector('dialog');
    this.body = this.querySelector('[data-sa-body]');
    if (!this.dialog || !this.body) return;

    /** Cached regions (with stores + stock) for the page view. */
    this.regions = null;

    this.querySelectorAll('.check-availability').forEach((button) => {
      button.addEventListener('click', () => this.open());
    });

    this.body.addEventListener('click', this.#onBodyClick);
    this.dialog.addEventListener('click', this.#onDialogClick);
    this.dialog.addEventListener('cancel', this.#onCancel);
    this.dialog.addEventListener('close', this.#onClose);
  }

  disconnectedCallback() {
    this.#activeFetch?.abort();
  }

  /* ---------------------------------------------------------------- open/close */

  open() {
    if (this.dialog.open) return;
    document.documentElement.classList.add('sa-scroll-lock');
    this.dialog.showModal();
    this.currentRegion = null;
    this.currentStore = null;
    this.#renderRegions();
  }

  close = () => {
    if (!this.dialog.open) return;
    this.dialog.classList.add('sa-closing');
    const finish = () => {
      this.dialog.classList.remove('sa-closing');
      this.dialog.removeEventListener('animationend', finish);
      this.dialog.close();
    };
    this.dialog.addEventListener('animationend', finish);
  };

  #onClose = () => {
    document.documentElement.classList.remove('sa-scroll-lock');
  };

  #onCancel = (event) => {
    // Intercept native ESC so we can play the slide-out animation.
    event.preventDefault();
    this.close();
  };

  #onDialogClick = (event) => {
    if (event.target === this.dialog) this.close();
  };

  #onBodyClick = (event) => {
    const sizeGuideTrigger = event.target.closest('.size-guide-drawer__trigger-open');
    if (sizeGuideTrigger) {
      this.close();
      return;
    }

    const action = event.target.closest('[data-sa-action]');
    if (!action) return;

    switch (action.dataset.saAction) {
      case 'close':
        this.close();
        break;
      case 'back-regions':
        this.currentRegion = null;
        this.currentStore = null;
        this.#renderRegions();
        break;
      case 'back-stores':
        this.currentStore = null;
        this.#renderStores();
        break;
      case 'region':
        this.currentRegion = this.regions?.find((region) => region.id === action.dataset.id) || null;
        this.#renderStores();
        break;
      case 'store':
        this.currentStore =
          this.currentRegion?.stores?.find((store) => String(store.id) === action.dataset.id) || null;
        this.#renderStock();
        break;
    }
  };

  /* ---------------------------------------------------------------- data */

  async #loadRegions() {
    if (this.regions) return this.regions;

    if (!this.storeSlug) throw new Error('missing store slug');

    const ids = this.variants.map((variant) => variant.id);
    if (!ids.length) {
      this.regions = [];
      return this.regions;
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const url = `${this.apiBase}/${encodeURIComponent(this.storeSlug)}/outlets?variant_ids=${ids.join(',')}`;
    console.log(url);
    const response = await fetch(url, {
      signal: this.#activeFetch.signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`outlets ${response.status}`);

    const data = await response.json();
    this.regions = this.#groupByRegion(Array.isArray(data.outlets) ? data.outlets : []);
    return this.regions;
  }

  /** Group flat outlets into the design's regions, keeping API order within each. */
  #groupByRegion(outlets) {
    const byId = new Map();

    for (const outlet of outlets) {
      const region = this.#regionFor(outlet);
      if (!byId.has(region.id)) byId.set(region.id, { id: region.id, name: region.name, stores: [] });
      byId.get(region.id).stores.push(this.#normalizeStore(outlet));
    }

    // Preserve the REGIONS order, dropping any region with no stores.
    return REGIONS.map((region) => byId.get(region.id)).filter(Boolean);
  }

  #regionFor(outlet) {
    const postcode = parseInt(String(outlet?.contact?.postcode || '').replace(/\D/g, ''), 10);
    const match = Number.isFinite(postcode) ? REGIONS.find((region) => postcode < region.max) : null;
    return match || REGIONS[REGIONS.length - 1];
  }

  #normalizeStore(outlet) {
    const contact = outlet.contact || {};
    const addressLines = [contact.address1, contact.address2, contact.address3].filter(Boolean);
    const cityLine = [contact.suburb, contact.postcode].filter(Boolean).join(' ').trim();
    if (cityLine) addressLines.push(cityLine);

    return {
      id: String(outlet.external_id),
      name: outlet.name || '',
      addressLines,
      phone: contact.phone || '',
      email: contact.email || '',
      stock: outlet.stock || {},
    };
  }

  /** Map a raw quantity to an in/low/out status. */
  #status(quantity) {
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) return 'out';
    if (qty <= this.lowThreshold) return 'low';
    return 'in';
  }

  /* ---------------------------------------------------------------- render */

  #renderRegions() {
    this.#setStep(1);
    this.#renderHeader({ title: this.#t('check_store_stock') });
    this.body.insertAdjacentHTML(
      'beforeend',
      `<div class="sa-step"><h3 class="sa-step__title">${esc(this.#t('select_region'))}</h3>
        <div class="sa-list" data-sa-rows></div></div>`
    );
    const rows = this.body.querySelector('[data-sa-rows]');

    this.#withData(
      rows,
      () => this.#loadRegions(),
      (regions) => {
        if (!regions.length) return this.#emptyMessage(rows, this.#t('no_stores'));
        rows.innerHTML = regions
          .map(
            (region) =>
              `<button type="button" class="sa-row" data-sa-action="region" data-id="${esc(region.id)}">
                <span>${esc(region.name)}</span>${this.#chevron()}
              </button>`
          )
          .join('');
      }
    );
  }

  #renderStores() {
    this.#setStep(2);
    const region = this.currentRegion;
    this.#renderHeader({
      title: this.#t('check_store_stock'),
      back: 'back-regions',
      backLabel: this.#t('all_regions'),
    });
    this.body.insertAdjacentHTML(
      'beforeend',
      `<div class="sa-step"><h3 class="sa-step__title">${esc(
        this.#t('select_store').replace('{region}', region?.name || '')
      )}</h3><div class="sa-list">${(region?.stores || [])
        .map(
          (store) =>
            `<button type="button" class="sa-row" data-sa-action="store" data-id="${esc(store.id)}">
              <span>${esc(store.name)}</span>${this.#chevron()}
            </button>`
        )
        .join('')}</div></div>`
    );
  }

  #renderStock() {
    this.#setStep(3);
    const store = this.currentStore;
    this.#renderHeader({
      title: this.#t('check_store_stock'),
      back: 'back-stores',
      backLabel: this.#t('all_stores'),
    });

    const contact = [...(store?.addressLines || []), store?.phone, store?.email]
      .filter(Boolean)
      .map((line) => `<div class="sa-store__line">${esc(line)}</div>`)
      .join('');

    this.body.insertAdjacentHTML(
      'beforeend',
      `<div class="sa-step sa-stock">
        <div class="sa-store">
          <h3 class="sa-store__name">${esc(store?.name || '')}</h3>
          ${contact}
        </div>
        <div class="sa-legend">
          <span class="sa-legend__label">${esc(this.#t('key'))}:</span>
          <div class="sa-legend__items">
          <span class="sa-legend__item"><i class="sa-dot sa-dot--in"></i><span>${esc(this.#t('in_stock'))}</span></span>
          <span class="sa-legend__item"><i class="sa-dot sa-dot--low"></i><span>${esc(this.#t('low_stock'))}</span></span>
          <span class="sa-legend__item"><i class="sa-dot sa-dot--out"></i><span>${esc(this.#t('out_of_stock'))}</span></span>
          </div>
        </div>
        ${this.#stockTable(store?.stock || {})}
        ${
          this.sizeGuideUrl || this.sizeGuidePage
            ? `<div class="sa-stock__footer">
                <span>${esc(this.#t('not_sure_size'))}</span>
                <a class="sa-stock__guide${this.sizeGuidePage ? ' size-guide-drawer__trigger-open' : ''}" 
                   href="${this.sizeGuidePage ? '#' : esc(this.sizeGuideUrl)}"
                   ${this.sizeGuidePage ? `data-size-guide-page="${esc(this.sizeGuidePage)}"` : ''}>
                  ${esc(this.#t('view_size_guide'))}
                </a>
              </div>`
            : ''
        }
      </div>`
    );
  }

  #stockTable(stock) {
    const lengths = this.lengths.length ? this.lengths : [''];
    const head = lengths.map((length) => `<th>${esc(length)}</th>`).join('');

    const rows = this.sizes
      .map((size) => {
        const cells = lengths
          .map((length) => {
            const variant = this.variants.find(
              (item) => item.size === size && (item.length || '') === length
            );
            let status = 'out';
            if (variant) {
              status = this.#status(stock[variant.id]);
            }
            const label = this.#t(STATUS[status].key);
            return `<td><i class="sa-dot sa-dot--${status}" title="${esc(label)}" aria-label="${esc(
              `${size} ${length} ${label}`.trim()
            )}"></i></td>`;
          })
          .join('');
        return `<tr><th scope="row">${esc(size)}</th>${cells}</tr>`;
      })
      .join('');

    return `<table class="sa-table">
      <thead><tr><th>${esc(this.#t('size'))}</th>${head}</tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
  }

  /* ---------------------------------------------------------------- render helpers */

  /** Flag the current step on the drawer body so each step can be styled. */
  #setStep(step) {
    this.body.classList.remove('step-1', 'step-2', 'step-3');
    this.body.classList.add(`step-${step}`);
    this.body.dataset.step = String(step);
  }

  #renderHeader({ title, back, backLabel }) {
    const left = back
    ? `<button type="button" class="sa-header__back" data-sa-action="${back}">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M12.5 3.75L6.25 10L12.5 16.25"
            stroke="black"
            stroke-linecap="round"
            stroke-linejoin="round"/>
        </svg>
        <span>${esc(backLabel)}</span>
      </button>`
    : `<span class="sa-header__title">${esc(title)}</span>`;

    this.body.innerHTML = `<div class="sa-header">${left}
      <button type="button" class="sa-header__close" data-sa-action="close" aria-label="${esc(
        this.#t('close')
      )}"><svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'><path d='M15.625 4.375L4.375 15.625' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/><path d='M15.625 15.625L4.375 4.375' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg></button>
    </div>`;
  }

  /** Render a spinner, run a loader, then hand the result to render(). */
  async #withData(target, loader, render) {
    target.innerHTML = `<div class="sa-loading">${esc(this.#t('loading'))}</div>`;
    try {
      const data = await loader();
      render(data);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      target.innerHTML = `<div class="sa-error">${esc(this.#t('error'))}</div>`;
    }
  }

  #emptyMessage(target, message) {
    target.innerHTML = `<div class="sa-empty">${esc(message)}</div>`;
  }

  #chevron(direction = 'right') {
    const path = direction === 'left' ? 'M15 18l-6-6 6-6' : 'M9 6l6 6-6 6';
    return `<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'><path d='M6 3L11 8L6 13' stroke='black' stroke-linecap='round' stroke-linejoin='round'/></svg>`;
  }

  /* ---------------------------------------------------------------- utilities */

  #t(key) {
    return this.labels[key] || key;
  }

  #readJson(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (_error) {
      return null;
    }
  }

  #unique(list) {
    const seen = new Set();
    const result = [];
    for (const value of list) {
      const normalized = value == null ? '' : String(value);
      if (!seen.has(normalized)) {
        seen.add(normalized);
        result.push(normalized);
      }
    }
    return result;
  }
}

if (!customElements.get('store-availability')) {
  customElements.define('store-availability', StoreAvailability);
}
